import { type FunctionComponent, type MouseEvent, useCallback, useMemo } from 'react';
import { useRouter } from 'next/router';
import { trackEvent } from '@surfline/web-common';

import SpotPreviewCardWithClarity from 'components/SpotPreviewCardWithClarity';
import type { Units } from 'types/units';
import type { UserFavorite } from 'types/userFavorites';
import { spotReportPath } from 'utils/urls';

import { getSnapAlignStyle } from './slideUtils';
import styles from './FavoritesCarouselSnap.module.scss';

interface FavoriteSlideProps {
  favorite: UserFavorite;
  index: number;
  isActive: boolean;
  snapPointIndexes: Set<number>;
  units: Units;
}

const FavoriteSlide: FunctionComponent<FavoriteSlideProps> = ({
  favorite,
  index,
  isActive,
  snapPointIndexes,
  units,
}) => {
  const { asPath, push: routerPush } = useRouter();
  const href = spotReportPath({}, { _id: favorite._id, name: favorite.name });

  const clipPathIds = useMemo(
    () => ({
      surfBarGraphIcon: `surf-bar-graph-icon-${index}`,
      windBarGraphIcon: `wind-bar-graph-icon-${index}`,
    }),
    [index],
  );

  const listItemStyle = useMemo(
    () => getSnapAlignStyle(index, snapPointIndexes),
    [index, snapPointIndexes],
  );

  const handleOnClick = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      const observedType = favorite.waveHeight?.type;
      trackEvent('Carousel Clicked', {
        carouselItem: favorite.name,
        carouselName: 'Favorites Carousel',
        category: 'kbyg',
        path: asPath,
        postId: favorite._id,
        positionNumber: index,
        title: 'Home',
        hasObservedWaveHeight: observedType === 'HUMAN' || observedType === 'CAMERA',
      });
      if (!(e.target as HTMLElement).closest('a')) {
        e.preventDefault();
        routerPush(href);
      }
    },
    [favorite, asPath, index, routerPush, href],
  );

  return (
    <li className={styles.item} data-testid="favorites-carousel-snap-card" style={listItemStyle}>
      <SpotPreviewCardWithClarity
        cardIndex={index}
        clipPathIds={clipPathIds}
        href={href}
        layout="FULL"
        onClickHandler={handleOnClick}
        spotReport={favorite}
        stopStream={!isActive}
        units={units}
      />
    </li>
  );
};

export default FavoriteSlide;
